/** Formatting in the tenant's locale/timezone — never the device timezone. */
export function formatMoney(minor: number, currency = 'RUB', locale = 'ru-RU'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: minor % 100 === 0 ? 0 : 2 }).format(minor / 100);
}

export function formatPriceRange(prices: number[], currency: string, locale: string, note?: string | null): string {
  if (note) return note;
  const valid = prices.filter((p) => Number.isFinite(p));
  if (valid.length === 0) return '—';
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  return min === max ? formatMoney(min, currency, locale) : `${formatMoney(min, currency, locale).replace(/\s?₽$/, '')} – ${formatMoney(max, currency, locale)}`;
}

/** "от 800 ₽" when prices differ by vehicle class, the exact price otherwise. */
export function formatPriceFrom(prices: number[], currency: string, locale: string, note?: string | null): string {
  if (note) return note;
  const valid = prices.filter((p) => Number.isFinite(p));
  if (valid.length === 0) return '—';
  const min = Math.min(...valid);
  return min === Math.max(...valid) ? formatMoney(min, currency, locale) : `от ${formatMoney(min, currency, locale)}`;
}

export function formatDuration(minutes: number): string {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const d = minutes / 1440;
    return `${d} ${plural(d, 'день', 'дня', 'дней')}`;
  }
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h} ч ${m} мин`;
  if (h) return `${h} ч`;
  return `${m} мин`;
}

export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

export function formatDateTime(iso: string, timeZone: string, locale = 'ru-RU'): string {
  return new Intl.DateTimeFormat(locale, { timeZone, weekday: 'short', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

export function formatTime(iso: string, timeZone: string, locale = 'ru-RU'): string {
  return new Intl.DateTimeFormat(locale, { timeZone, hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

/** "2026-10-08" → "чт, 8 окт." (date-only values are wall dates; format in UTC). */
export function formatDay(day: string, locale = 'ru-RU', opts: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }): string {
  return new Intl.DateTimeFormat(locale, { ...opts, timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`));
}

export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function minutesToHHMM(min: number): string {
  if (min === 1440) return '24:00';
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

export function hhmmToMinutes(v: string): number {
  const [h = '0', m = '0'] = v.split(':');
  return Number(h) * 60 + Number(m);
}

const WEEKDAY_NAMES = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
export function weekdayName(isoWeekday: number): string {
  return WEEKDAY_NAMES[isoWeekday - 1] ?? '';
}

export interface HoursRow { weekday: number; open_minute: number; close_minute: number }

/** Compact human schedule: "Ежедневно 24/7", "Пн–Сб 09:00–21:00 · Вс выходной". */
export function describeHours(hours: HoursRow[]): string {
  if (hours.length === 7 && hours.every((h) => h.open_minute === 0 && h.close_minute === 1440)) return 'Круглосуточно, без выходных';
  const byDay = new Map(hours.map((h) => [h.weekday, `${minutesToHHMM(h.open_minute)}–${minutesToHHMM(h.close_minute)}`]));
  const groups: { from: number; to: number; label: string }[] = [];
  for (let d = 1; d <= 7; d++) {
    const label = byDay.get(d) ?? 'выходной';
    const last = groups.at(-1);
    if (last && last.label === label && last.to === d - 1) last.to = d;
    else groups.push({ from: d, to: d, label });
  }
  return groups.map((g) => `${g.from === g.to ? weekdayName(g.from) : `${weekdayName(g.from)}–${weekdayName(g.to)}`} ${g.label}`).join(' · ');
}
